import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsSummary, setNotificationsSummary] = useState(null);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/full-profile");
        setUser(data);

        const tenantRes = await api.get("/tenant/current");
        setTenant(tenantRes.data);

        await reloadNotifications();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    check();
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", {
      identifier,
      password,
    });

    if (data.token) {
      localStorage.setItem("local_token", data.token);
    }

    const me = await api.get("/auth/full-profile");
    setUser(me.data);

    const tenantRes = await api.get("/tenant/current");
    setTenant(tenantRes.data.tenant);

    await reloadNotifications();

    return me.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }

    localStorage.removeItem("local_token");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/full-profile");

      setUser(data);
    } catch {
      setUser(null);
    }
  };

  const permissions = useMemo(
    () => (Array.isArray(user?.permissions) ? user.permissions : []),
    [user],
  );

  const roleDetails = useMemo(
    () => (Array.isArray(user?.role_details) ? user.role_details : []),
    [user],
  );

  const isAdmin = useMemo(() => permissions.includes("*"), [permissions]);

  const can = (perm) => {
    if (isAdmin) return true;

    return permissions.includes(perm);
  };

  const canAny = (perms) => {
    if (isAdmin) return true;

    return Array.isArray(perms) && perms.some((p) => permissions.includes(p));
  };

  /**
   * Comprueba si el usuario tiene una determinada permission
   * dentro de un scope concreto.
   *
   * Ejemplo:
   *
   * canScope("manage_eventos", "eventos", "festejos")
   *
   * Un rol sin scopes se considera global.
   */
  const canScope = (perm, scopeType, scopeValue) => {
    if (isAdmin) return true;

    const matchingRoles = roleDetails.filter((role) => {
      const rolePermissions = Array.isArray(role.permissions)
        ? role.permissions
        : [];

      return rolePermissions.includes("*") || rolePermissions.includes(perm);
    });

    if (matchingRoles.length === 0) {
      return false;
    }

    return matchingRoles.some((role) => {
      const scopes = role.scopes;

      // Rol sin scopes = acceso global
      if (!scopes) {
        return true;
      }

      const allowedValues = scopes[scopeType];

      // Si el scope no está definido = acceso global
      if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
        return true;
      }

      // "*" = acceso global
      if (allowedValues.includes("*")) {
        return true;
      }

      return allowedValues.includes(scopeValue);
    });
  };

  const hasModule = (moduleName) => {
    return tenant?.modules?.[moduleName] === true;
  };

  const reloadNotifications = async () => {
    try {
      const res = await api.get("/notificaciones/summary");

      setNotificationsSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        permissions,
        roleDetails,
        can,
        canAny,
        canScope,
        isAdmin,
        notificationsSummary,
        reloadNotifications,
        refreshUser,
        tenant,
        hasModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
